/**
 * Carding QC Form — Layer 2.
 *
 * Two purposes on one screen:
 *   • Sliver QC observations (hank/CV%/U%/neps) per card-shift —
 *     stored in /checklist-logs entry_type=CARD_QC.
 *   • Bulk QC action on the cans the spine just generated — uses the
 *     existing /cans/bulk/qc-status endpoint.
 *
 * The bulk action is the productivity pivot: instead of clicking 17
 * cans one by one, the QC officer selects "all pending for this card
 * + shift" and hits one button.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Col, DatePicker, Form, Input, InputNumber,
  Modal, Row, Select, Space, Table, Tag, Typography,
} from 'antd'
import {
  CheckCircleOutlined, StopOutlined, WarningOutlined,
  PauseCircleOutlined, CheckOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'
import PageHeader from '@/shared/components/PageHeader'
import { filterActiveShifts } from '@/shared/constants/shifts'
import { plantsApi } from '@/features/masters/plants/api'
import { machinesApi } from '@/features/masters/machines/api'
import { shiftsApi } from '@/features/gate/api'
import {
  cansApi, checklistLogsApi, laydownApi,
} from '@/features/baleManagement/api'
import { ReadOnlyTile, StatusPill } from './_shared'

const { Text } = Typography

const AUTOLEVELLER_STATUS = ['OK', 'NOT_OK']
const QC_DECISIONS = [
  { value: 'PASS',             label: 'PASS' },
  { value: 'HOLD',             label: 'HOLD' },
  { value: 'SETTINGS_REVIEW',  label: 'SETTINGS REVIEW' },
  { value: 'REJECTED',         label: 'REJECTED' },
]

export default function CardingQCPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [plantId, setPlantId] = useState<string | undefined>()
  const [machineId, setMachineId] = useState<string | undefined>()
  const [shiftId, setShiftId] = useState<string | undefined>()
  const [laydownId, setLaydownId] = useState<string | undefined>()
  const [productionDate, setProductionDate] = useState<Dayjs>(dayjs())
  const [targetHank, setTargetHank] = useState<number | null>(null)
  const [actualHank, setActualHank] = useState<number | null>(null)
  const [selectedCanIds, setSelectedCanIds] = useState<string[]>([])
  const [bulkRemarkOpen, setBulkRemarkOpen] = useState<{
    status: string; can_ids: string[];
  } | null>(null)
  const [bulkRemark, setBulkRemark] = useState('')

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })
  const plantOptions = plantsData?.items?.map((p: any) => ({ label: p.name, value: p.id })) ?? []

  const { data: machinesData } = useQuery({
    queryKey: ['machines-cd-qc', plantId],
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 200 }),
    enabled: !!plantId,
  })
  const cardMachines = (machinesData?.items || []).filter((m: any) => {
    const t = (m.section || '').toUpperCase()
    return /^CARD/.test(t) || t === 'CARDING'
  })

  const { data: shifts = [] } = useQuery({
    queryKey: ['shifts-all-active'],
    queryFn: async () => filterActiveShifts(await shiftsApi.list()),
  })

  const { data: laydowns = [] } = useQuery({
    queryKey: ['laydowns-active', plantId],
    queryFn: () => laydownApi.list({ plant_id: plantId, limit: 50 }),
    enabled: !!plantId,
  })
  const laydownOptions = (Array.isArray(laydowns) ? laydowns : [])
    .filter((l: any) => ['RELEASED', 'CONDITIONING', 'READY_FOR_MIX', 'MIXING_IN_PROGRESS', 'MIXING_COMPLETED', 'MIXING_CONSUMED'].includes(l.status))
    .map((l: any) => ({
      label: `${l.laydown_no} — ${l.count_description || 'no count'} [${l.status}]`,
      value: l.id,
    }))

  // Cans for the selected (card, date, shift, laydown) filter set.
  const canQuery = useQuery({
    queryKey: ['cans-for-qc', plantId, machineId, productionDate.format('YYYY-MM-DD'), shiftId, laydownId],
    queryFn: () => cansApi.list({
      plant_id: plantId,
      card_machine_id: machineId,
      shift_id: shiftId,
      laydown_id: laydownId,
      production_date: productionDate.format('YYYY-MM-DD'),
      limit: 500,
    }),
    enabled: !!plantId,
  })
  const cans: any[] = canQuery.data || []

  const counts = useMemo(() => {
    const c: Record<string, number> = {
      total: cans.length, PENDING: 0, PASSED: 0, HELD: 0,
      RELEASED: 0, REJECTED: 0,
    }
    for (const x of cans) c[x.qc_status] = (c[x.qc_status] || 0) + 1
    return c
  }, [cans])

  const selectedKg = useMemo(
    () => cans.filter(c => selectedCanIds.includes(c.id))
              .reduce((s, c) => s + (c.net_kg || 0), 0),
    [cans, selectedCanIds],
  )
  const selectedValue = useMemo(
    () => cans.filter(c => selectedCanIds.includes(c.id))
              .reduce((s, c) => s + (c.total_can_cost || 0), 0),
    [cans, selectedCanIds],
  )

  const hankDeviation = useMemo(() => {
    if (targetHank == null || actualHank == null || targetHank <= 0) return null
    return ((actualHank - targetHank) / targetHank) * 100
  }, [targetHank, actualHank])

  // ── Mutations ────────────────────────────────────────────────────
  const bulkMutation = useMutation({
    mutationFn: (body: { can_ids: string[]; new_status: string; remark?: string }) =>
      cansApi.bulkUpdateQc(body),
    onSuccess: (res: any) => {
      message.success(
        `${res.updated || 0} can(s) updated, ${res.skipped || 0} skipped`,
      )
      setSelectedCanIds([])
      setBulkRemarkOpen(null)
      setBulkRemark('')
      qc.invalidateQueries({ queryKey: ['cans-for-qc'] })
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Bulk QC failed'),
  })

  const obsMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => checklistLogsApi.upsert(body),
    onSuccess: () => message.success('Sliver QC observation saved'),
    onError: (e: any) => message.error(e.response?.data?.detail || 'Save failed'),
  })

  // ── Quick selectors ───────────────────────────────────────────────
  const selectAllPending = () => {
    setSelectedCanIds(
      cans.filter(c => c.qc_status === 'PENDING').map(c => c.id)
    )
  }
  const selectAllForLaydown = () => {
    if (!laydownId) {
      message.warning('Select a laydown first')
      return
    }
    setSelectedCanIds(
      cans.filter(c => c.laydown_plan_id === laydownId).map(c => c.id)
    )
  }
  const selectAll = () => setSelectedCanIds(cans.map(c => c.id))
  const clearSelection = () => setSelectedCanIds([])

  // ── Bulk action triggers ──────────────────────────────────────────
  const triggerBulk = (status: string) => {
    if (selectedCanIds.length === 0) {
      message.warning('Select at least one can')
      return
    }
    // HOLD / REJECTED require a reason; PASS / RELEASED don't but we
    // still surface the modal so the QC officer can add context.
    if (status === 'HELD' || status === 'REJECTED') {
      setBulkRemarkOpen({ status, can_ids: selectedCanIds })
    } else {
      bulkMutation.mutate({ can_ids: selectedCanIds, new_status: status })
    }
  }

  const submitObservation = async () => {
    const v = await form.validateFields()
    if (!plantId || !shiftId) {
      message.error('Plant + shift required')
      return
    }
    obsMutation.mutate({
      plant_id: plantId,
      log_date: productionDate.format('YYYY-MM-DD'),
      entry_type: 'CARD_QC',
      shift_id: shiftId,
      machine_id: machineId || null,
      laydown_plan_id: laydownId || null,
      items_json: {
        target_sliver_hank: targetHank,
        actual_sliver_hank: actualHank,
        hank_deviation_pct: hankDeviation != null ? Number(hankDeviation.toFixed(3)) : null,
        sliver_cv_pct: v.sliver_cv_pct ?? null,
        u_pct: v.u_pct ?? null,
        neps_per_g: v.neps_per_g ?? null,
        trash_observation: v.trash_observation || null,
        waste_pct: v.waste_pct ?? null,
        waste_quality: v.waste_quality || null,
        autoleveller_status: v.autoleveller_status || null,
        can_quality: v.can_quality || null,
      },
      decision: v.decision || null,
      corrective_action: v.corrective_action || null,
      remarks: v.remarks || null,
    })
  }

  // Reset selection if filters change.
  useEffect(() => { setSelectedCanIds([]) }, [machineId, laydownId, shiftId, productionDate])

  const cols = [
    { title: 'Can No', dataIndex: 'can_no', width: 200,
      render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
    { title: 'Net kg', dataIndex: 'net_kg', width: 90, align: 'right' as const,
      render: (v: number, r: any) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {Number(v).toFixed(2)}{r.is_partial_can && <Tag color="orange" style={{ fontSize: 9, marginLeft: 4 }}>partial</Tag>}
        </span>
      ) },
    { title: 'QC', dataIndex: 'qc_status', width: 100,
      render: (v: string) => <StatusPill status={v} /> },
    { title: 'Movement', dataIndex: 'movement_status', width: 140,
      render: (v: string) => <StatusPill status={v} /> },
    { title: 'Cost / kg', dataIndex: 'total_can_cost_per_kg', width: 100, align: 'right' as const,
      render: (v: number | null) => v != null ? `₹${Number(v).toFixed(2)}` : '—' },
    { title: 'Total ₹', dataIndex: 'total_can_cost', width: 110, align: 'right' as const,
      render: (v: number | null) => v != null ? `₹${Number(v).toFixed(2)}` : <Text type="secondary">—</Text> },
  ]

  return (
    <div>
      <PageHeader
        title="Carding — QC"
        subtitle="Bulk QC actions on cans + per-card-shift sliver observation. Use the existing Layer 1 endpoints — zero one-by-one clicking."
      />

      <Card>
        <Row gutter={12}>
          <Col xs={24} md={5}>
            <Form.Item label="Plant"><Select options={plantOptions} value={plantId} onChange={setPlantId} /></Form.Item>
          </Col>
          <Col xs={24} md={5}>
            <Form.Item label="Card">
              <Select
                options={cardMachines.map((m: any) => ({
                  label: `${m.machine_id_str} — ${m.name}`, value: m.id,
                }))}
                value={machineId} onChange={setMachineId}
                disabled={!plantId} showSearch optionFilterProp="label" allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item label="Shift">
              <Select
                options={(shifts || []).map((s: any) => ({
                  label: s.shift_name || s.shift_code, value: s.id,
                }))}
                value={shiftId} onChange={setShiftId} allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item label="Date">
              <DatePicker
                value={productionDate} onChange={(d) => d && setProductionDate(d)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Laydown">
              <Select
                options={laydownOptions} value={laydownId} onChange={setLaydownId}
                showSearch optionFilterProp="label" allowClear
                placeholder={laydownOptions.length ? 'Select laydown' : 'No active laydowns'}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={12} md={4}><ReadOnlyTile label="Total cans" value={counts.total} source="SYSTEM" /></Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <div style={{ fontSize: 11, color: '#666' }}>PENDING</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#666' }}>{counts.PENDING}</div>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <div style={{ fontSize: 11, color: '#666' }}>PASSED</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#52c41a' }}>{counts.PASSED}</div>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <div style={{ fontSize: 11, color: '#666' }}>HELD</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fa8c16' }}>{counts.HELD}</div>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <div style={{ fontSize: 11, color: '#666' }}>RELEASED</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#13c2c2' }}>{counts.RELEASED}</div>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <div style={{ fontSize: 11, color: '#666' }}>REJECTED</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#ff4d4f' }}>{counts.REJECTED}</div>
          </Card>
        </Col>
      </Row>

      <Card title="Cans for this card / shift" style={{ marginTop: 12 }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <Button size="small" onClick={selectAllPending}>
            Select all PENDING ({counts.PENDING})
          </Button>
          <Button size="small" onClick={selectAllForLaydown} disabled={!laydownId}>
            Select all for laydown
          </Button>
          <Button size="small" onClick={selectAll}>Select all visible</Button>
          <Button size="small" onClick={clearSelection}>Clear</Button>
          <Text type="secondary" style={{ marginLeft: 12 }}>
            {selectedCanIds.length} selected · {selectedKg.toFixed(2)} kg ·{' '}
            {selectedValue > 0 ? `₹${selectedValue.toFixed(2)}` : 'cost pending'}
          </Text>
        </Space>

        <Space wrap style={{ marginBottom: 12 }}>
          <Button
            type="primary" icon={<CheckCircleOutlined />}
            disabled={selectedCanIds.length === 0}
            loading={bulkMutation.isPending}
            onClick={() => triggerBulk('PASSED')}
          >
            Pass selected
          </Button>
          <Button
            icon={<PauseCircleOutlined />}
            danger={false}
            disabled={selectedCanIds.length === 0}
            loading={bulkMutation.isPending}
            onClick={() => triggerBulk('HELD')}
            style={{ background: '#fa8c16', color: '#fff', borderColor: '#fa8c16' }}
          >
            Hold selected
          </Button>
          <Button
            icon={<CheckOutlined />} type="dashed"
            disabled={selectedCanIds.length === 0}
            loading={bulkMutation.isPending}
            onClick={() => triggerBulk('RELEASED')}
          >
            Release selected
          </Button>
          <Button
            icon={<StopOutlined />} danger
            disabled={selectedCanIds.length === 0}
            loading={bulkMutation.isPending}
            onClick={() => triggerBulk('REJECTED')}
          >
            Reject selected
          </Button>
        </Space>

        <Table
          rowKey="id"
          columns={cols}
          dataSource={cans}
          loading={canQuery.isLoading}
          size="small"
          pagination={{ pageSize: 50 }}
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys: selectedCanIds,
            onChange: (keys) => setSelectedCanIds(keys as string[]),
            getCheckboxProps: (r: any) => ({
              disabled: r.movement_status === 'TRANSFERRED_TO_DRAW_FRAME'
                       || r.qc_status === 'REJECTED',
            }),
          }}
        />
      </Card>

      <Card title="Sliver QC observation (this card / shift)" style={{ marginTop: 12 }}>
        <Form layout="vertical" form={form} size="middle">
          <Row gutter={12}>
            <Col xs={12} md={4}>
              <Form.Item label="Target hank">
                <InputNumber
                  style={{ width: '100%' }} step={0.01} min={0}
                  value={targetHank} onChange={(v) => setTargetHank(typeof v === 'number' ? v : null)}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item label="Actual hank">
                <InputNumber
                  style={{ width: '100%' }} step={0.01} min={0}
                  value={actualHank} onChange={(v) => setActualHank(typeof v === 'number' ? v : null)}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <ReadOnlyTile
                label="Hank deviation"
                value={hankDeviation != null ? `${hankDeviation.toFixed(2)}%` : '—'}
                source="SYSTEM"
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="sliver_cv_pct" label="Sliver CV %">
                <InputNumber style={{ width: '100%' }} step={0.1} min={0} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="u_pct" label="U %">
                <InputNumber style={{ width: '100%' }} step={0.01} min={0} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="neps_per_g" label="Neps / g">
                <InputNumber style={{ width: '100%' }} step={1} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={12} md={6}>
              <Form.Item name="trash_observation" label="Trash / seed coat">
                <Input placeholder="e.g. seed coat visible" />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="waste_pct" label="Waste %">
                <InputNumber style={{ width: '100%' }} step={0.1} min={0} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="waste_quality" label="Waste quality">
                <Select
                  options={['LOW_FIBER_LOSS', 'MEDIUM_FIBER_LOSS', 'HIGH_FIBER_LOSS']
                    .map(v => ({ value: v, label: v.replace(/_/g, ' ') }))}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="autoleveller_status" label="Autoleveller">
                <Select options={AUTOLEVELLER_STATUS.map(v => ({ value: v, label: v }))} allowClear />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="can_quality" label="Can quality">
                <Select
                  options={['CLEAN', 'MIXED_LOT', 'DIRTY_CAN', 'DAMAGED_SLIVER']
                    .map(v => ({ value: v, label: v.replace(/_/g, ' ') }))}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={6}>
              <Form.Item name="decision" label="Overall QC decision">
                <Select options={QC_DECISIONS} placeholder="Optional summary" allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={18}>
              <Form.Item name="corrective_action" label="Corrective action">
                <Input placeholder="e.g. autoleveller calibration" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remarks" label="QC remarks">
            <Input.TextArea rows={2} maxLength={300} />
          </Form.Item>
          <Button
            type="primary" icon={<CheckCircleOutlined />}
            loading={obsMutation.isPending} onClick={submitObservation}
          >
            Save sliver QC observation
          </Button>
        </Form>
      </Card>

      {/* Reason modal for HOLD / REJECTED. */}
      <Modal
        title={
          <Space>
            {bulkRemarkOpen?.status === 'HELD' ? <PauseCircleOutlined /> : <WarningOutlined />}
            <span>Reason — {bulkRemarkOpen?.status} {bulkRemarkOpen?.can_ids.length} can(s)</span>
          </Space>
        }
        open={!!bulkRemarkOpen}
        onCancel={() => { setBulkRemarkOpen(null); setBulkRemark('') }}
        onOk={() => {
          if (!bulkRemark || bulkRemark.trim().length < 5) {
            message.error('Reason must be at least 5 characters')
            return
          }
          if (bulkRemarkOpen) {
            bulkMutation.mutate({
              can_ids: bulkRemarkOpen.can_ids,
              new_status: bulkRemarkOpen.status,
              remark: bulkRemark.trim(),
            })
          }
        }}
        okText={`Confirm ${bulkRemarkOpen?.status}`}
        okButtonProps={{ danger: bulkRemarkOpen?.status === 'REJECTED' }}
        confirmLoading={bulkMutation.isPending}
      >
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message={`This logs to audit + flips ${bulkRemarkOpen?.can_ids.length} can(s).`}
        />
        <Input.TextArea
          rows={3} value={bulkRemark} onChange={(e) => setBulkRemark(e.target.value)}
          placeholder="Reason (≥5 chars) — e.g. Sliver CV exceeded limit; cards #6 and #11"
        />
      </Modal>
    </div>
  )
}
